import React, { useEffect, useRef, useState } from "react";
import { Plane, MapPin, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";

/**
 * PlacePicker — autocomplete input for cities & airports.
 * Hits `/api/geocode/suggest?q=…` (curated IATA + Nominatim city fallback).
 *
 * Value shape:  { city: "Mumbai", iata: "BOM"|null, lat, lng, kind }
 * onChange:     receives the same shape or `null` when cleared.
 *
 * Renders as a text input; a dropdown shows suggestions after 2+ chars.
 * Keyboard: ↑↓ navigate, Enter select, Esc close.
 *
 * Uncontrolled text; controlled selection.
 */
export default function PlacePicker({
  value,               // { city, iata?, lat, lng, kind? } | null
  onChange,
  placeholder = "City or airport",
  icon,
  testid = "place-picker",
  className = "",
  autoFocus = false,
}) {
  const [text, setText] = useState(() => _displayFor(value));
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const inputRef = useRef(null);
  const boxRef   = useRef(null);
  const debTimer = useRef(null);

  // If value is set externally (e.g., swap or prefill), reflect it.
  useEffect(() => {
    setText(_displayFor(value));
  }, [value?.city, value?.iata]);

  // Debounced fetch
  useEffect(() => {
    if (debTimer.current) clearTimeout(debTimer.current);
    const q = text.trim();
    if (q.length < 2) { setResults([]); setLoading(false); return; }
    // Skip fetching if the current text is already the display of the value
    if (value && text === _displayFor(value)) { setResults([]); return; }
    setLoading(true);
    debTimer.current = setTimeout(async () => {
      try {
        const r = await api.get("/geocode/suggest", { params: { q, limit: 6 } });
        setResults(r.data?.results || []);
        setOpen(true);
        setActive(-1);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 220);
    return () => debTimer.current && clearTimeout(debTimer.current);
  }, [text, value?.city, value?.iata]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Outside click closes
  useEffect(() => {
    const onDoc = (e) => {
      if (!boxRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (row) => {
    onChange?.({
      city:         row.city,
      iata:         row.iata || null,
      lat:          row.lat,
      lng:          row.lng,
      kind:         row.kind,
      country_code: row.country_code,
    });
    setText(_displayFor(row));
    setOpen(false);
    setResults([]);
  };

  const onKey = (e) => {
    if (!open || !results.length) {
      if (e.key === "Enter" && text.trim().length >= 2) setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); setActive(a => Math.min(results.length - 1, a + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive(a => Math.max(0, a - 1)); }
    else if (e.key === "Enter")  {
      e.preventDefault();
      pick(results[active >= 0 ? active : 0]);
    }
    else if (e.key === "Escape") { setOpen(false); }
  };

  const clear = () => {
    onChange?.(null);
    setText("");
    setResults([]);
    setOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div ref={boxRef} className={`relative ${className}`} data-testid={testid}>
      <div className="flex items-center gap-2 rounded-xl border border-stone-300 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 bg-white px-3 py-3">
        {icon}
        <input
          ref={inputRef}
          type="text"
          value={text}
          onChange={(e) => { setText(e.target.value); setOpen(true); }}
          onFocus={() => text.trim().length >= 2 && setOpen(true)}
          onKeyDown={onKey}
          placeholder={placeholder}
          autoFocus={autoFocus}
          autoComplete="off"
          className="flex-1 bg-transparent focus:outline-none text-stone-900 placeholder:text-stone-400"
          data-testid={`${testid}-input`}
        />
        {loading && <Loader2 size={14} className="animate-spin text-stone-400" />}
        {text && !loading && (
          <button
            type="button"
            onClick={clear}
            className="p-1 rounded hover:bg-stone-100 text-stone-400 hover:text-stone-600"
            aria-label="Clear"
            data-testid={`${testid}-clear`}
          >
            <X size={14} />
          </button>
        )}
      </div>

      {open && results.length > 0 && (
        <div
          className="absolute left-0 right-0 top-full mt-1 bg-white border border-stone-200 rounded-xl shadow-lg overflow-hidden z-50 max-h-80 overflow-y-auto"
          data-testid={`${testid}-dropdown`}
        >
          {results.map((r, i) => (
            <button
              key={`${r.kind}-${r.iata || r.city}-${i}`}
              type="button"
              onMouseEnter={() => setActive(i)}
              onClick={() => pick(r)}
              className={`w-full text-left px-3 py-2.5 flex items-center gap-3 border-b border-stone-100 last:border-b-0 ${
                active === i ? "bg-blue-50" : "hover:bg-stone-50"
              }`}
              data-testid={`${testid}-option-${i}`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                r.kind === "airport" ? "bg-sky-100 text-sky-700" : "bg-stone-100 text-stone-600"
              }`}>
                {r.kind === "airport" ? <Plane size={14} /> : <MapPin size={14} />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-stone-900 truncate">{r.label}</div>
                <div className="text-[11px] text-stone-500 truncate">{r.sub_label}</div>
              </div>
              {r.iata && (
                <div className="text-[10px] font-black tracking-wider text-sky-700 bg-sky-50 border border-sky-100 rounded px-1.5 py-0.5 shrink-0">
                  {r.iata}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function _displayFor(v) {
  if (!v) return "";
  if (v.iata) return `${v.city} (${v.iata})`;
  return v.city || v.label || "";
}
